import type {ZodTypeAny} from 'zod';
import type {ZodType} from 'zod/v4';

/**
 * Creates a Zod schema that accepts `T | null | undefined` but produces
 * `T | undefined`. This is useful as a replacement for `.optional()` when you
 * want to accept null values in the input but filter them out from the
 * output type.
 *
 * @example
 * ```typescript
 * const Foo = z.object({
 *   name: optionalNonNull(z.string())
 * });
 * // Input type:  {name: string | undefined | null}
 * // Output type: {name: string | undefined}
 *
 * Foo.parse("hello");   // {name: "hello"}
 * Foo.parse(undefined); // {name: undefined}
 * Foo.parse(null);      // {name: undefined}
 * ```
 */
export function optionalNonNull<T extends ZodTypeAny>(schema: T) {
  return schema
    .nullable()
    .optional()
    .transform((val) => (val === null ? undefined : val));
}

export function optionalNonNullZ4<T extends ZodType>(schema: T) {
  return schema
    .nullable()
    .transform((val) => (val === null ? undefined : val))
    .optional();
}
